<template lang="pug">
    .wa-templates
        //- Section header: a quiet label with its Meta-review caption grouped beneath it.
        //- The add action lives here ONLY when templates already exist — when the list is
        //- empty the guided empty state below owns the single call to action.
        .wa-templates__head
            .wa-templates__heading
                span.wa-templates__title {{ $t('Message templates') }}
                span.wa-templates__caption {{ $t('New templates are reviewed by Meta before they can be used.') }}
            el-button.wa-templates__add(
                v-if="canUpdateMessages && templates.length && !showForm"
                size="mini" type="success" plain icon="el-icon-fa-plus fas"
                @click="showForm = true"
            ) {{ $t('New template') }}

        el-form.wa-templates__form(v-if="showForm" label-position="top" :model="form" :rules="rules" ref="templateForm")
            el-form-item(:label="$t('Name')" prop="name")
                //- Name is immutable at Meta once created, so it is locked while editing.
                el-input(:value="form.name" :disabled="!!editing" @input="onNameInput")
                span.help {{ editing ? $t('The name cannot be changed after creation.') : $t('Only lowercase letters, numbers and underscores (required by Meta).') }}
            .wa-templates__row
                el-form-item(:label="$t('Category')")
                    el-select(v-model="form.category" style="width: 100%")
                        el-option(v-for="category in categories" :key="category" :label="$t('category.' + category.toLowerCase())" :value="category")
                el-form-item(:label="$t('Language')")
                    el-select(v-model="form.language" filterable style="width: 100%")
                        el-option(v-for="lang in languages" :key="lang.code" :label="lang.name" :value="lang.code")
            //- Header (none/text/image), footer and buttons — the shared interactive builder,
            //- the same authoring surface the composer and auto-reply use. The Message editor
            //- goes in its slot so the form follows the real card order: header, message, footer,
            //- buttons.
            whatsapp-interactive-builder(:value="interactiveModel" :allow-order="hasShop" @input="applyInteractive")
                el-form-item(:label="$t('Message')")
                    quill-editor.wa-templates__editor(ref="bodyEditor" v-model="form.body" :options="whatsappQuillConfigWithLink")
                    span.help {{ $t('Use the {…} button in the toolbar to add a placeholder, or the link button to insert a storefront link.') }}
            //- Live preview of the whole card (header, body with example values, footer, buttons).
            //- Frame + "Vorschau" label come from the shared component.
            whats-app-card-preview.wa-templates__preview(
                v-if="strippedBody || form.header || form.footer || renderableButtons.length || headerImageUrl"
                framed
                :header-image="form.header_format === 'IMAGE' ? headerImageUrl : ''"
                :header="form.header_format === 'IMAGE' ? '' : form.header"
                :body="previewHtml"
                :footer="form.footer"
                :buttons="renderableButtons"
            )
            .wa-templates__formfoot
                el-button(size="mini" @click="cancelForm") {{ $t('Cancel') }}
                el-button(size="mini" type="success" :loading="saving" :disabled="!canSubmit" @click="create") {{ editing ? $t('Resubmit for approval') : $t('Submit for approval') }}

        .wa-templates__list(v-loading="loading")
            //- "Could not load" is not "no templates": say so, with Meta's reason.
            el-alert.wa-templates__load-error(
                v-if="loadError"
                type="warning"
                :closable="false"
                show-icon
                :title="$t('The templates could not be loaded from Meta. Reconnect the WhatsApp number.')"
                :description="loadError"
            )
            //- Guided empty state: a soft channel-green badge, the status, one line of
            //- context, and the single primary action — instead of a lonely centered line.
            .wa-templates__empty(v-if="!loading && !loadError && !templates.length && !showForm")
                span.wa-templates__empty-badge
                    i.el-icon-fa-comment-dots.far
                span.wa-templates__empty-title {{ $t('No templates yet.') }}
                span.wa-templates__empty-hint {{ $t('Templates let you reach customers outside the 24-hour reply window.') }}
                el-button(v-if="canUpdateMessages" type="success" plain size="small" icon="el-icon-fa-plus fas" @click="showForm = true") {{ $t('New template') }}

            .wa-template(v-for="template in templates" :key="template.name")
                .wa-template__main
                    span.wa-template__name {{ template.name }}
                    span.wa-template__meta {{ $t('category.' + (template.category || '').toLowerCase()) }} • {{ template.language }}
                    span.wa-template__body {{ template.body }}
                    //- A rejected template shows Meta's reason (the only detail the API gives)
                    //- plus a human hint, and can be fixed + resubmitted in place.
                    span.wa-template__rejection(v-if="(template.status || '').toUpperCase() === 'REJECTED'")
                        i.el-icon-fa-circle-exclamation.fas
                        |  {{ rejectionHint(template.rejected_reason) }}
                .wa-template__side
                    span.wa-template__status(:class="'is-' + (template.status || '').toLowerCase()") {{ $t('status.' + (template.status || '').toLowerCase()) }}
                    //- Meta blocks editing a template while it is PENDING review (only a
                    //- rejected or approved one can be edited), so the pencil is hidden then.
                    el-button.wa-template__edit(
                        v-if="canUpdateMessages && template.id && (template.status || '').toUpperCase() !== 'PENDING'"
                        size="mini" type="text" icon="el-icon-fa-pen fas"
                        @click="startEdit(template)"
                    )
                    el-button.wa-template__delete(v-if="canDeleteMessages" size="mini" type="text" icon="el-icon-fa-trash fas" @click="$refs.deleteDialog.open(template)")
        confirm-delete-dialog(ref="deleteDialog" :title="$t('Delete template')" :message="$t('The template will be deleted at Meta and can no longer be sent.')" @confirm="remove")
</template>

<i18n>
{
    "de": {
        "Message templates": "Nachrichtenvorlagen",
        "New templates are reviewed by Meta before they can be used.": "Neue Vorlagen werden von Meta geprüft, bevor sie verwendet werden können.",
        "New template": "Neue Vorlage",
        "Ticket number": "Ticketnummer",
        "Name": "Name",
        "Category": "Kategorie",
        "Language": "Sprache",
        "Message": "Nachricht",
        "Header": "Überschrift",
        "Text": "Text",
        "Image": "Bild",
        "Remove image": "Bild entfernen",
        "Replace image": "Bild ersetzen",
        "Header image": "Überschriftenbild",
        "Open media library": "Medienbibliothek öffnen",
        "Shown at the top of the message; a wide image looks best.": "Wird oben in der Nachricht angezeigt; ein breites Bild wirkt am besten.",
        "A short bold title above the message.": "Eine kurze fette Überschrift über der Nachricht.",
        "Footer": "Fußzeile",
        "A small note under the message.": "Ein kleiner Hinweis unter der Nachricht.",
        "Buttons": "Schaltflächen",
        "Link": "Link",
        "Quick reply": "Schnellantwort",
        "Call": "Anruf",
        "Button text": "Text der Schaltfläche",
        "Add up to 3 buttons: a link, a quick reply, or a phone number.": "Füge bis zu 3 Schaltflächen hinzu: einen Link, eine Schnellantwort oder eine Telefonnummer.",
        "Add button": "Schaltfläche hinzufügen",
        "Only lowercase letters, numbers and underscores (required by Meta).": "Nur Kleinbuchstaben, Zahlen und Unterstriche (von Meta vorgegeben).",
        "Use the {…} button in the toolbar to add a placeholder; it is filled with a real value when you send.": "Mit der Schaltfläche {…} in der Symbolleiste fügst du einen Platzhalter ein; er wird beim Senden mit einem echten Wert gefüllt.",
        "Search placeholder": "Platzhalter suchen …",
        "Use the {…} button in the toolbar to add a placeholder, or the link button to insert a storefront link.": "Nutze die Schaltfläche {…} in der Symbolleiste für einen Platzhalter oder die Link-Schaltfläche, um einen Storefront-Link einzufügen.",
        "Insert": "Einfügen",
        "External": "Extern",
        "Email": "E-Mail",
        "Pages": "Seiten",
        "Media library": "Medienbibliothek",
        "Choose from library": "Aus Bibliothek wählen",
        "Preview": "Vorschau",
        "Customer name": "Kundenname",
        "Member name": "Mitgliedsname",
        "Recipient name": "Name des Empfängers",
        "Order number": "Bestellnummer",
        "Order date": "Bestelldatum",
        "Order status": "Bestellstatus",
        "Tracking number": "Sendungsnummer",
        "Amount": "Betrag",
        "Submit for approval": "Zur Freigabe einreichen",
        "Resubmit for approval": "Erneut zur Freigabe einreichen",
        "The name cannot be changed after creation.": "Der Name kann nach der Erstellung nicht geändert werden.",
        "Cancel": "Abbrechen",
        "No templates yet.": "Noch keine Vorlagen.",
        "The templates could not be loaded from Meta. Reconnect the WhatsApp number.": "Die Vorlagen konnten nicht von Meta geladen werden. Verbinde die WhatsApp-Nummer neu.",
        "Templates let you reach customers outside the 24-hour reply window.": "Mit Vorlagen erreichst du Kunden auch außerhalb des 24-Stunden-Antwortfensters.",
        "Delete template": "Vorlage löschen",
        "The template will be deleted at Meta and can no longer be sent.": "Die Vorlage wird bei Meta gelöscht und kann nicht mehr gesendet werden.",
        "Template submitted for approval": "Vorlage zur Freigabe eingereicht",
        "Template resubmitted for approval": "Vorlage erneut zur Freigabe eingereicht",
        "Template deleted": "Vorlage gelöscht",
        "reject.invalid_format": "Abgelehnt: ungültiges Format. Prüfe die Variablen-Beispiele, ihre Platzierung und die Formatierung.",
        "reject.abusive_content": "Abgelehnt: Inhalt wurde als missbräuchlich eingestuft.",
        "reject.scam": "Abgelehnt: als Betrug eingestuft.",
        "reject.promotional": "Abgelehnt: werblicher Inhalt ist für diese Kategorie nicht erlaubt.",
        "reject.tag_content_mismatch": "Abgelehnt: Inhalt passt nicht zur gewählten Kategorie.",
        "reject.unknown": "Von Meta abgelehnt ({reason}).",
        "category.utility": "Servicenachricht",
        "category.marketing": "Marketing",
        "category.authentication": "Authentifizierung",
        "status.approved": "Freigegeben",
        "status.pending": "In Prüfung",
        "status.rejected": "Abgelehnt"
    },
    "en": {
        "category.utility": "Utility",
        "category.marketing": "Marketing",
        "category.authentication": "Authentication",
        "status.approved": "Approved",
        "status.pending": "Pending",
        "status.rejected": "Rejected",
        "reject.invalid_format": "Rejected: invalid format. Check the variable examples, their placement, and the formatting.",
        "reject.abusive_content": "Rejected: content flagged as abusive.",
        "reject.scam": "Rejected: flagged as a scam.",
        "reject.promotional": "Rejected: promotional content is not allowed for this category.",
        "reject.tag_content_mismatch": "Rejected: content does not match the chosen category.",
        "reject.unknown": "Rejected by Meta ({reason})."
    }
}
</i18n>

<script>
import WhatsAppTemplateApi from '@/extensions/communication/store/api/whatsappTemplate'
import baseWhatsappQuillConfig from '@/quill/whatsappConfig'
import isoLanguages from '@cospired/i18n-iso-languages'
import { useSetupStore } from '@/stores'
import { checkRestrictions } from '@/helpers'
import { whatsappPlaceholderOptions, ORDER_DETAILS_TOKEN, ORDER_DETAILS_SUFFIX_KEY, ORDER_TRACKING_TOKEN, ORDER_TRACKING_SUFFIX_KEY } from '@/extensions/communication/utils/whatsappPlaceholders'
import WhatsAppInteractiveBuilder from '@/extensions/communication/views/Partials/WhatsAppInteractiveBuilder'
import WhatsAppCardPreview from '@/extensions/communication/views/Partials/WhatsAppCardPreview'
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog'
import whatsAppLinkTool from '@/extensions/communication/mixins/whatsAppLinkTool'
import { headerPreviewUrl, sanitizeHeaderImageInput } from '@/extensions/communication/utils/headerImageInput'

// The standard ISO 639-1 language list (localized names), the same source the CMS
// settings use — not an arbitrary curated set.
isoLanguages.registerLocale(require('@cospired/i18n-iso-languages/langs/de.json'))
isoLanguages.registerLocale(require('@cospired/i18n-iso-languages/langs/en.json'))

export default {
    name: 'WhatsAppTemplateManager',
    mixins: [whatsAppLinkTool],
    // Explicit kebab key so the <whatsapp-interactive-builder> tag resolves (Vue would otherwise
    // auto-kebab the name to "whats-app-interactive-builder").
    components: { 'whatsapp-interactive-builder': WhatsAppInteractiveBuilder, 'whats-app-card-preview': WhatsAppCardPreview, ImageLibraryDialog, ConfirmDeleteDialog },
    props: {
        accountId: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            templates: [],
            loading: false,
            loadError: null,
            saving: false,
            showForm: false,
            // vee-validate (VeeElement el-form :rules, pipe-string) — Meta requires the
            // snake_case format; enforced here, not just hinted.
            rules: {
                name: 'required|regex:^[a-z0-9_]+$'
            },
            categories: ['UTILITY', 'MARKETING', 'AUTHENTICATION'],
            // The template being edited (null = creating a new one); holds its Meta id for
            // the in-place edit + resubmit.
            editing: null,
            form: this.emptyForm()
        }
    },
    computed: {
        // Templates are messages: writing and resubmitting one is the update
        // flag on ex:comm:messages, deleting one the delete flag.
        canUpdateMessages() {
            return checkRestrictions(['ex:comm:messages'], 'update')
        },
        canDeleteMessages() {
            return checkRestrictions(['ex:comm:messages'], 'delete')
        },
        // Browser-displayable URL for the picked header image: the token-authenticated
        // access URL while we still hold the File object (fresh pick), else the stored
        // The header preview url: the FileController served crop (private-safe) when a header
        // image is authored, else a loaded/approved template's stored header url.
        headerImageUrl() {
            return headerPreviewUrl(this.form.header_image) || this.form.header_media_url || ''
        },
        // The interactive chrome (header/footer/buttons) as the shared builder's v-model, read
        // from the flat form fields; applyInteractive writes edits back so submit stays unchanged.
        interactiveModel() {
            return {
                header_format: this.form.header_format,
                header: this.form.header,
                header_image: this.form.header_image,
                header_media_url: this.form.header_media_url,
                footer: this.form.footer,
                buttons: this.form.buttons
            }
        },
        // WhatsApp templates are keyed by a language CODE; show the localized name and
        // submit the code. Sourced from the ISO language set (same as CMS settings).
        languages() {
            const locale = (this.$i18n && this.$i18n.locale) || 'de'
            const names = isoLanguages.getNames(locale)
            return Object.keys(names)
                .map(code => ({ code, name: names[code] }))
                .sort((a, b) => a.name.localeCompare(b.name, locale))
        },
        // The body is edited in Quill, so it is HTML; strip tags to check for real content.
        strippedBody() {
            return (this.form.body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
        },
        // Buttons with actual text, for the preview (an in-progress empty row isn't shown).
        renderableButtons() {
            return this.form.buttons.filter(button => (button.text || '').trim())
        },
        canSubmit() {
            return /^[a-z0-9_]+$/.test(this.form.name) && !!this.form.language && this.strippedBody.length > 0
        },
        // The named placeholders a user can insert, from the shared definition list (so the
        // create editor and the send composer never drift). Feature-aware: order fields only
        // with the shop, recipient labelled for what the tenant has. Each carries a stable
        // `key` we persist with the template so the composer can label + pre-fill it.
        placeholderOptions() {
            const enabledExtensions = useSetupStore().enabledExtensions
            return whatsappPlaceholderOptions({
                t: this.$t.bind(this),
                hasShop: enabledExtensions.includes('shop'),
                hasMembers: enabledExtensions.includes('shop') || enabledExtensions.includes('restricted-area'),
                hasTickets: enabledExtensions.includes('tickets')
            })
        },
        // Order-related authoring (the "Order details" dynamic button) only makes sense with
        // the shop; the LinkPicker further gates on the storefront declaring an order link.
        hasShop() {
            return useSetupStore().enabledExtensions.includes('shop')
        },
        // The Quill options with the shared placeholder tool wired up: a `{{ … }}`
        // pill dropped from a searchable toolbar dropdown, exactly like the signature
        // editor. `value` = the friendly label, so the pill reads "{{ Kundenname }}" and
        // the same label drives the preview + the server-side positional numbering.
        whatsappQuillConfig() {
            return {
                ...baseWhatsappQuillConfig,
                modules: {
                    ...baseWhatsappQuillConfig.modules,
                    placeholder: {
                        variables: this.placeholderOptions.map(option => ({ label: option.label, value: option.label })),
                        placeholder: this.$t('Search placeholder')
                    }
                }
            }
        },
        // The body config PLUS the shared link tool (whatsAppLinkTool mixin) — the same tool the
        // composer uses, so a template body can carry a storefront link alongside its placeholders.
        whatsappQuillConfigWithLink() {
            return this.withWhatsAppLinkTool(this.whatsappQuillConfig)
        },
        // The message as the customer will receive it. It mirrors the SERVER formatter
        // (WhatsAppTextFormatter) exactly, so what you see is what gets sent: a paragraph
        // boundary becomes a blank line, a soft <br> becomes a single line break, and
        // bold/italic stay visible (WhatsApp renders them too). Placeholder pills are
        // shown filled with their example value, highlighted.
        previewHtml() {
            // Private-use code points as sentinels: they stand in for the markup we want
            // to survive the tag strip, and can't collide with anything a user types.
            const marker = code => String.fromCharCode(0xE000 + code)
            const FILL_OPEN = marker(0)
            const FILL_CLOSE = marker(1)
            const OPEN = { strong: marker(2), em: marker(4), s: marker(6) }
            const CLOSE = { strong: marker(3), em: marker(5), s: marker(7) }

            let text = this.form.body || ''

            // Pills to their example value, marked for highlighting.
            text = text.replace(/<span\b[^>]*class="[^"]*\bql-twig\b[^"]*"[^>]*>[\s\S]*?<\/span>/gi, span => {
                const match = span.match(/data-variable="([^"]*)"/)
                return FILL_OPEN + this.exampleFor(match ? match[1] : '') + FILL_CLOSE
            })

            // Emphasis we keep (as sentinels, restored after the strip).
            text = text
                .replace(/<\s*(strong|b)\s*>/gi, OPEN.strong).replace(/<\/\s*(strong|b)\s*>/gi, CLOSE.strong)
                .replace(/<\s*(em|i)\s*>/gi, OPEN.em).replace(/<\/\s*(em|i)\s*>/gi, CLOSE.em)
                .replace(/<\s*(s|del|strike)\s*>/gi, OPEN.s).replace(/<\/\s*(s|del|strike)\s*>/gi, CLOSE.s)

            // Block structure to line breaks, exactly like the server WhatsAppTextFormatter:
            // a soft <br> is one line break, a paragraph or list boundary is a blank line.
            text = text
                .replace(/<br\b[^>]*>/gi, '\n')
                .replace(/<\s*li[^>]*>/gi, '\n- ')
                .replace(/<\/\s*(p|div|h[1-6]|blockquote)\s*>/gi, '\n\n')

            // Drop every remaining tag, decode entities, collapse and trim.
            const decoder = document.createElement('textarea')
            text = text.replace(/<[^>]+>/g, '')
            decoder.innerHTML = text
            text = decoder.value.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()

            // Re-escape as plain text, then restore the highlight + emphasis markup.
            const escaper = document.createElement('div')
            escaper.textContent = text
            return escaper.innerHTML
                .split(FILL_OPEN).join('<span class="wa-fill">').split(FILL_CLOSE).join('</span>')
                .split(OPEN.strong).join('<strong>').split(CLOSE.strong).join('</strong>')
                .split(OPEN.em).join('<em>').split(CLOSE.em).join('</em>')
                .split(OPEN.s).join('<s>').split(CLOSE.s).join('</s>')
        }
    },
    mounted() {
        this.fetch()
    },
    methods: {
        emptyForm() {
            return { name: '', category: 'UTILITY', language: 'de', body: '', header: '', header_format: 'TEXT', header_media_url: '', header_image: null, footer: '', buttons: [] }
        },
        // Write the shared builder's edits back onto the flat form fields (submit reads them).
        applyInteractive(next) {
            this.form.header_format = next.header_format
            this.form.header = next.header
            this.form.header_image = next.header_image
            this.form.header_media_url = next.header_media_url
            this.form.footer = next.footer
            this.form.buttons = next.buttons
        },
        // Map the authored buttons to the mutation shape. Most URLs are a fixed storefront
        // link from the shared LinkPicker; a button whose picked link is the order-details
        // token becomes a DYNAMIC per-order URL button — the backend bakes the storefront
        // base + {{1}} and fills the signed suffix (order.details_url_suffix) at send time.
        // buttonVariableKeys is position-aligned (null = a static / non-URL button).
        buildButtonsPayload() {
            const buttons = this.form.buttons.map(button => ({
                type: button.type,
                text: button.text,
                url: button.url,
                phone_number: button.phone_number
            }))
            const keyByToken = {
                [ORDER_DETAILS_TOKEN]: ORDER_DETAILS_SUFFIX_KEY,
                [ORDER_TRACKING_TOKEN]: ORDER_TRACKING_SUFFIX_KEY
            }
            const buttonVariableKeys = this.form.buttons.map(button =>
                (button.type === 'URL' && keyByToken[button.url]) || null
            )
            return { buttons, buttonVariableKeys }
        },
        removeButton(index) {
            this.form.buttons.splice(index, 1)
        },
        // WhatsApp template names must be snake_case (lowercase, digits, underscores).
        // Normalise as the user types so they never have to think about the format.
        onNameInput(value) {
            this.form.name = (value || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        },
        // The example value shown for a placeholder label in the preview.
        exampleFor(label) {
            const found = this.placeholderOptions.find(option => option.label === label)
            return found ? found.example : (label || '…')
        },
        // The ordered stable keys behind the body's pills (index 0 = {{1}}), read straight
        // off the inserted pills in DOM order and mapped label → key. Persisted with the
        // template so the send composer can label + pre-fill each variable.
        extractVariableKeys() {
            const keyByLabel = this.placeholderOptions.reduce((map, option) => {
                map[option.label] = option.key
                return map
            }, {})
            const container = document.createElement('div')
            container.innerHTML = this.form.body || ''
            return Array.from(container.querySelectorAll('.ql-twig'))
                .map(pill => keyByLabel[pill.getAttribute('data-variable')])
                .filter(Boolean)
        },
        // The ordered EXAMPLE values behind the body's pills (index 0 = {{1}}), read in the
        // same DOM order as extractVariableKeys so they stay aligned. Meta requires one
        // sample per body variable; without them it rejects the template as INVALID_FORMAT.
        extractVariableExamples() {
            const exampleByLabel = this.placeholderOptions.reduce((map, option) => {
                map[option.label] = option.example
                return map
            }, {})
            const container = document.createElement('div')
            container.innerHTML = this.form.body || ''
            return Array.from(container.querySelectorAll('.ql-twig'))
                .map(pill => exampleByLabel[pill.getAttribute('data-variable')])
                .filter(value => value !== undefined && value !== null)
        },
        // Meta only exposes a coarse rejection enum; pair it with a human hint so the agent
        // knows what to fix before resubmitting.
        rejectionHint(reason) {
            const code = (reason || '').toUpperCase()
            const known = {
                INVALID_FORMAT: this.$t('reject.invalid_format'),
                ABUSIVE_CONTENT: this.$t('reject.abusive_content'),
                SCAM: this.$t('reject.scam'),
                PROMOTIONAL: this.$t('reject.promotional'),
                TAG_CONTENT_MISMATCH: this.$t('reject.tag_content_mismatch')
            }
            return known[code] || this.$t('reject.unknown', { reason: code || '?' })
        },
        placeholderLabelForKey(key) {
            const found = this.placeholderOptions.find(option => option.key === key)
            return found ? found.label : null
        },
        // Positional {{n}} body text back into the editor's label pills (reverse of the send
        // numbering) and its line structure into HTML, so a rejected template reopens with
        // its placeholders intact for fixing. Bold/italic markers stay literal; they already
        // round-trip through the server formatter unchanged.
        bodyToEditorHtml(body, variableKeys) {
            const keys = variableKeys || []
            // Escape the body's own text FIRST so a literal "<", ">" or "&" (e.g. "Preis <50€",
            // "Tom & Jerry") can't be parsed as markup when set as the editor's HTML. Pills are
            // injected after, from our controlled placeholder labels, so they stay real markup.
            const escaped = (body || '').replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]))
            const withPills = escaped.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, index) => {
                const label = this.placeholderLabelForKey(keys[parseInt(index, 10) - 1])
                return label
                    ? `<span class="ql-twig" data-variable="${label}" contenteditable="false">{{ ${label} }}</span>`
                    : match
            })
            return withPills.split(/\n{2,}/).map(block => `<p>${block.replace(/\n/g, '<br>')}</p>`).join('')
        },
        // Reopen an existing template (usually a rejected one) in the form to fix + resubmit.
        // Name and language are immutable at Meta, so they are locked; the body, header,
        // footer and buttons are reconstructed from its stored components.
        startEdit(template) {
            const components = template.components || []
            const byType = type => components.find(component => (component.type || '').toUpperCase() === type)
            const headerComponent = byType('HEADER')
            const footerComponent = byType('FOOTER')
            const buttonsComponent = byType('BUTTONS')
            const headerFormat = (template.header_format || (headerComponent && headerComponent.format) || 'TEXT').toUpperCase()
            const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)

            this.form = {
                name: template.name,
                category: template.category || 'UTILITY',
                language: template.language || 'de',
                body: this.bodyToEditorHtml(template.body, template.variable_keys),
                header: isMediaHeader ? '' : ((headerComponent && headerComponent.text) || ''),
                header_format: isMediaHeader ? headerFormat : 'TEXT',
                header_media_url: template.header_media_url || '',
                // Rehydrate the saved editable header Image so the shared builder can RE-CROP it.
                header_image: _.get(template, 'headerImage') || null,
                footer: (footerComponent && footerComponent.text) || '',
                // A dynamic order-details button comes back as a baked base/{{1}} URL; restore
                // it to the order token so the LinkPicker reopens on "Order details" (its stored
                // key at this position marks it) instead of showing the raw external URL.
                buttons: ((buttonsComponent && buttonsComponent.buttons) || []).map((button, index) => {
                    const tokenByKey = {
                        [ORDER_DETAILS_SUFFIX_KEY]: ORDER_DETAILS_TOKEN,
                        [ORDER_TRACKING_SUFFIX_KEY]: ORDER_TRACKING_TOKEN
                    }
                    const dynamicToken = tokenByKey[(template.button_variable_keys || [])[index]]
                    return {
                        type: (button.type || 'QUICK_REPLY').toUpperCase(),
                        text: button.text || '',
                        url: dynamicToken || (button.url || ''),
                        phone_number: button.phone_number || ''
                    }
                })
            }
            this.editing = template
            this.showForm = true
        },
        /**
         * Open the form on a recommended template rather than a blank one. The
         * shape of a support template is not obvious — Utility category, the
         * ticket variables, and quick replies whose tap reopens the 24h window —
         * so it is shown and left fully editable rather than described.
         */
        startFromPreset(preset) {
            const { body, variable_keys: variableKeys, ...rest } = preset || {}

            this.editing = null
            this.form = {
                ...this.emptyForm(),
                ...rest,
                // The preset carries Meta's positional body; the editor works in
                // label pills, so it goes through the same conversion a reopened
                // template does. Every slot must be a KNOWN placeholder: Meta
                // rejects a template whose variable has no example value.
                body: this.bodyToEditorHtml(body || '', variableKeys)
            }
            this.showForm = true
        },
        cancelForm() {
            this.showForm = false
            this.editing = null
            this.form = this.emptyForm()
        },
        fetch() {
            this.loading = true
            this.loadError = null
            WhatsAppTemplateApi.fetchAll(this.accountId)
                .then(templates => { this.templates = templates })
                .catch(error => { this.loadError = this.graphqlMessage(error) })
                .finally(() => { this.loading = false })
        },
        create() {
            this.$refs.templateForm.validate(valid => {
                if (valid && this.canSubmit) this.submitTemplate()
            })
        },
        submitTemplate() {
            if (!this.canSubmit) return
            this.saving = true
            const { buttons, buttonVariableKeys } = this.buildButtonsPayload()
            const payload = {
                name: this.form.name,
                category: this.form.category,
                language: this.form.language,
                body: this.form.body,
                header: this.form.header || null,
                header_format: this.form.header_format,
                header_media_url: this.form.header_media_url || null,
                // The authored editable image (ImageInput) — persisted server-side so the header
                // can be re-cropped later; the backend derives Meta's media url from its baked file.
                headerImage: sanitizeHeaderImageInput(this.form.header_image),
                footer: this.form.footer || null,
                buttons,
                variable_keys: this.extractVariableKeys(),
                variable_examples: this.extractVariableExamples(),
                button_variable_keys: buttonVariableKeys
            }
            // Editing a rejected/existing template resubmits it in place (Meta re-reviews);
            // otherwise a new template is created. Both go back to PENDING.
            const request = this.editing
                ? WhatsAppTemplateApi.edit(this.accountId, this.editing.id, payload)
                : WhatsAppTemplateApi.create(this.accountId, payload)
            request
                .then(() => {
                    this.notifySuccess(this.$t(this.editing ? 'Template resubmitted for approval' : 'Template submitted for approval'))
                    this.cancelForm()
                    this.fetch()
                })
                .catch(error => this.notifyError(this.graphqlMessage(error)))
                .finally(() => { this.saving = false })
        },
        remove(template) {
            WhatsAppTemplateApi.remove(this.accountId, template.name)
                .then(() => {
                    this.notifySuccess(this.$t('Template deleted'))
                    this.templates = this.templates.filter(item => item.name !== template.name)
                })
                .catch(error => this.notifyError(this.graphqlMessage(error)))
        },
        graphqlMessage(error) {
            const messages = (error && error.graphQLErrors) || []
            return (messages[0] && messages[0].message) || this.$t('Something went wrong!')
        },
        notifySuccess(message) {
            this.$notify({ title: message, type: 'success', position: 'bottom-left', customClass: 'dark', iconClass: 'el-icon-fa-check fas', duration: 8000 })
        },
        notifyError(message) {
            this.$notify({ title: message, type: 'error', position: 'bottom-left', customClass: 'dark', iconClass: 'el-icon-fa-triangle-exclamation fas', duration: 8000 })
        }
    }
}
</script>

<style lang="sass" scoped>
.wa-templates
    // Header: the section label with its caption stacked beneath it, action pinned right.
    .wa-templates__head
        display: flex
        align-items: flex-start
        justify-content: space-between
        gap: 12px
    .wa-templates__heading
        display: flex
        flex-direction: column
        gap: 3px
        min-width: 0
    .wa-templates__title
        font-size: .9rem
        font-weight: 600
        color: #303133
    .wa-templates__caption
        font-size: .72rem
        line-height: 1.45
        color: #909399
    .wa-templates__add
        flex-shrink: 0
    // Project hint convention: small, grey, tight line-height directly under the field
    // (el-form-item__content inherits 36px line-height otherwise).
    .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
        margin-top: 4px
    // Create form: a quiet card, not a flat grey slab.
    .wa-templates__form
        margin: 14px 0 4px
        padding: 16px
        background: #fafbfc
        border: 1px solid #ebeef5
        border-radius: 10px
        .wa-templates__row
            display: flex
            gap: 12px
            > *
                flex: 1
        .wa-templates__editor
            ::v-deep .ql-container
                border-bottom-left-radius: 6px
                border-bottom-right-radius: 6px
            ::v-deep .ql-toolbar
                border-top-left-radius: 6px
                border-top-right-radius: 6px
            ::v-deep .ql-editor
                min-height: 96px
                font-size: 13px
        // Header, footer and buttons authoring now lives in the shared WhatsAppInteractiveBuilder.
        // Live preview: the FULL card the customer receives; panel + label come from the shared
        // WhatsAppCardPreview (framed), we only own the spacing here.
        .wa-templates__preview
            margin-top: 12px
        .wa-templates__formfoot
            display: flex
            justify-content: flex-end
            gap: 8px
            margin-top: 14px
    .wa-templates__list
        margin-top: 6px
        // Reserve height so the v-loading spinner (an absolute overlay) has room to sit
        // in during the initial fetch instead of collapsing onto the header above.
        min-height: 120px
        position: relative
    // Guided empty state — the channel-green badge ties it to the connection card above.
    .wa-templates__empty
        display: flex
        flex-direction: column
        align-items: center
        text-align: center
        gap: 10px
        padding: 44px 20px 40px
        .wa-templates__empty-badge
            display: flex
            align-items: center
            justify-content: center
            width: 56px
            height: 56px
            margin-bottom: 2px
            border-radius: 50%
            background: rgba(103, 194, 58, .1)
            color: #67c23a
            font-size: 22px
        .wa-templates__empty-title
            font-size: .92rem
            font-weight: 600
            color: #303133
        .wa-templates__empty-hint
            max-width: 300px
            font-size: .78rem
            line-height: 1.5
            color: #909399
        .el-button
            margin-top: 6px
    .wa-template
        display: flex
        align-items: center
        justify-content: space-between
        gap: 12px
        padding: 12px 0
        border-top: 1px solid #f0f2f5
        &:first-of-type
            border-top: none
        .wa-template__main
            display: flex
            flex-direction: column
            gap: 2px
            min-width: 0
            .wa-template__name
                font-weight: 600
                font-size: .85rem
                color: #303133
            .wa-template__meta
                font-size: .72rem
                color: #909399
            .wa-template__body
                font-size: .78rem
                color: #606266
                overflow: hidden
                text-overflow: ellipsis
                white-space: nowrap
            .wa-template__rejection
                margin-top: 3px
                font-size: .72rem
                line-height: 1.4
                color: #f56c6c
                i
                    margin-right: 4px
                    font-size: .68rem
        .wa-template__side
            display: flex
            align-items: center
            gap: 10px
            flex-shrink: 0
            .wa-template__status
                font-size: .68rem
                font-weight: 600
                letter-spacing: .2px
                padding: 3px 9px
                border-radius: 20px
                &.is-approved
                    color: #67c23a
                    background: #f0f9eb
                &.is-pending
                    color: #e6a23c
                    background: #fdf6ec
                &.is-rejected
                    color: #f56c6c
                    background: #fef0f0
            .wa-template__edit
                color: #c0c4cc
                &:hover
                    color: #409EFF
            .wa-template__delete
                color: #c0c4cc
                &:hover
                    color: #f56c6c
</style>
